import { useMemo, useState, useCallback } from 'react';
import * as Yup from 'yup';
import useSWR from 'swr';
import { SERVICES } from '@shared/constants/apiConfig';
import { PhoenixGrid } from '@shared/components/grid/PhoenixGrid';
import { TableModal } from '@shared/components/modals/TableModal';
import { useUsers } from '@shared/hooks/useUsers';
import { Wrapper } from '@styles/CommonStyles';
import { stateUsersColumns, stateUsersFormConfig } from './constants';
import { IStateUser, IStateDropdownUser } from './interfaces';
import { requestCreateStateUser, requestDeleteStateUser, requestUpdateStateUser } from './queries';

const stateUsersSchema = Yup.object().shape({
  state: Yup.string().required(),
  users: Yup.array().of(Yup.string().required()).min(1, 'User are required'),
});

const StateUsers = () => {
  const { data: stateUsersList, error: stateUsersListError } = useSWR(`${SERVICES.USER}/stateuser`);
  const columns = useMemo(() => stateUsersColumns, []);
  const { users } = useUsers();
  const [currentData, setCurrentData] = useState<IStateDropdownUser>();
  const [openModal, setOpenModal] = useState(false);
  const [isSubmitting, setSubmitting] = useState(false);

  const handleAddNewRecordBtn = useCallback(async (data: IStateDropdownUser) => {
    setSubmitting(true);

    if (data.id) {
      await requestUpdateStateUser(data);
    } else {
      await requestCreateStateUser(data);
    }

    setSubmitting(false);
    setOpenModal(false);
  }, []);

  const handleNewBtn = useCallback(() => {
    setOpenModal(true);
    setCurrentData({} as IStateDropdownUser);
  }, []);

  const gridActions = {
    onEdit: (data: IStateUser) => {
      const mapUsers = data.users.map((user) => user.userId);
      setCurrentData({ ...data, users: mapUsers });
      setOpenModal(true);
    },
    onDelete: (data: IStateUser) => {
      requestDeleteStateUser(data?.id);
    },
  };

  return (
    <Wrapper>
      <TableModal
        isOpen={!!openModal}
        editingData={currentData as IStateDropdownUser}
        formConfig={stateUsersFormConfig(users)}
        validationSchema={stateUsersSchema}
        isSubmitting={isSubmitting}
        onSubmit={handleAddNewRecordBtn}
        onClose={() => setOpenModal(false)}
      />
      <PhoenixGrid
        autoHeight
        withHeader
        canNotCopy
        buttonTitle="Add New Record"
        name="State User Detail"
        columns={columns}
        loading={!!stateUsersList && !!stateUsersListError}
        actions={gridActions}
        rows={stateUsersList}
        onButtonClick={handleNewBtn}
      />
    </Wrapper>
  );
};

export default StateUsers;
